import type { StudioProjectOutputKind } from "@/features/project/lib/studio-project-plan";
import {
  getStudioRenewalRailItems,
  type StudioAuthorityDomain,
  type StudioAuthorityTask,
  type StudioRenewalRailItem,
} from "@/shell/ide-v2/studio-authority-workspaces";
import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales"
import { getCoogoAsIsPaneCopy, getCoogoIdeRailLabel } from '@/shell/ide-v2/coogo-ide-renewal-copy'

export interface StudioAuthorityTaskCopy {
  label: string;
  description: string;
}

const EN_TASKS = {
  "primary.app.overview": {
    label: "Overview",
    description: "Preview and operate the application",
  },
  "primary.app.structure": {
    label: "Structure",
    description: "Organize pages, blocks, and elements",
  },
  "primary.app.behavior": {
    label: "Behavior",
    description: "Define events and ActionFlows",
  },
  "primary.app.data": {
    label: "Data",
    description: "Connect data sources and bindings",
  },
  "primary.app.test": {
    label: "Test",
    description: "Verify the application result",
  },
  "primary.document.overview": {
    label: "Overview",
    description: "Preview the document artifact",
  },
  "primary.document.structure": {
    label: "Structure",
    description: "Organize document pages and sections",
  },
  "primary.document.content": {
    label: "Content",
    description: "Write and revise document content",
  },
  "primary.document.sources": {
    label: "Sources",
    description: "Manage document evidence and citations",
  },
  "primary.document.export": {
    label: "Export",
    description: "Prepare native document exports",
  },
  "primary.game.overview": {
    label: "Overview",
    description: "Preview and play the game",
  },
  "primary.game.scenes": {
    label: "Scenes",
    description: "Organize scenes and game screens",
  },
  "primary.game.rules": {
    label: "Rules",
    description: "Define game rules and ActionFlows",
  },
  "primary.game.data": {
    label: "Data",
    description: "Manage game state and data",
  },
  "primary.game.playtest": {
    label: "Playtest",
    description: "Verify playable behavior",
  },
  // FINAL-REVIEW M2 + overclaim relabel. The As-Is task had no entry here at
  // all, so every locale fell through to the raw English literal in
  // studio-authority-workspaces.ts. The wording says "read from the
  // original" rather than "the original screen": the artifact is a structure
  // readback (field names, column headers, action labels), not a
  // reproduction.
  "as-is.original": {
    label: "Original structure",
    description: "Review the structure read from the original — not a reproduction of it",
  },
  "design.system": {
    label: "System",
    description: "Manage tokens and design-system rules",
  },
  "design.sources": {
    label: "Sources",
    description: "Review Figma and visual source evidence",
  },
  "design.components": {
    label: "Components",
    description: "Review reusable components and variants",
  },
  "design.states": {
    label: "States",
    description: "Review responsive and accessible states",
  },
  "design.compare": {
    label: "Compare",
    description: "Compare source and generated structure",
  },
  "guide.overview": {
    label: "Overview",
    description: "Review companion guidance",
  },
  "guide.content": {
    label: "Content",
    description: "Write onboarding and operating guidance",
  },
  "guide.audiences": {
    label: "Audiences",
    description: "Adapt guidance for user roles",
  },
  "guide.localization": {
    label: "Localization",
    description: "Manage localized guidance",
  },
  "guide.publish": {
    label: "Publish",
    description: "Prepare approved guide outputs",
  },
  "insights.overview": {
    label: "Status",
    description: "Review readiness and ontology coverage",
  },
  "insights.sufficiency": {
    label: "Scope",
    description: "Assess whether source evidence is sufficient",
  },
  "insights.quality": {
    label: "Checks",
    description: "Review UI, behavior, and artifact quality",
  },
  "insights.runtime": {
    label: "Runtime",
    description: "Inspect runtime traces and failures",
  },
  "insights.release": {
    label: "Export",
    description: "Review release blockers and proof",
  },
  "insights.live": {
    label: "Live",
    description: "Review production feedback and checks",
  },
} as const;

export type StudioAuthorityCopyKey = keyof typeof EN_TASKS;
type StudioAuthorityTaskCopies = Record<
  StudioAuthorityCopyKey,
  StudioAuthorityTaskCopy
>;

const KO_TASKS: StudioAuthorityTaskCopies = {
  "primary.app.overview": {
    label: "개요",
    description: "애플리케이션을 미리 보고 실행합니다",
  },
  "primary.app.structure": {
    label: "구조",
    description: "페이지, 블록, 요소를 구성합니다",
  },
  "primary.app.behavior": {
    label: "동작",
    description: "이벤트와 ActionFlow를 정의합니다",
  },
  "primary.app.data": {
    label: "데이터",
    description: "데이터 소스와 바인딩을 연결합니다",
  },
  "primary.app.test": {
    label: "테스트",
    description: "애플리케이션 결과를 검증합니다",
  },
  "primary.document.overview": {
    label: "개요",
    description: "문서 결과물을 미리 봅니다",
  },
  "primary.document.structure": {
    label: "구조",
    description: "문서 페이지와 섹션을 구성합니다",
  },
  "primary.document.content": {
    label: "콘텐츠",
    description: "문서 내용을 작성하고 수정합니다",
  },
  "primary.document.sources": {
    label: "출처",
    description: "문서 근거와 인용을 관리합니다",
  },
  "primary.document.export": {
    label: "내보내기",
    description: "네이티브 문서 내보내기를 준비합니다",
  },
  "primary.game.overview": {
    label: "개요",
    description: "게임을 미리 보고 플레이합니다",
  },
  "primary.game.scenes": {
    label: "장면",
    description: "장면과 게임 화면을 구성합니다",
  },
  "primary.game.rules": {
    label: "규칙",
    description: "게임 규칙과 ActionFlow를 정의합니다",
  },
  "primary.game.data": {
    label: "데이터",
    description: "게임 상태와 데이터를 관리합니다",
  },
  "primary.game.playtest": {
    label: "플레이테스트",
    description: "실행 가능한 동작을 검증합니다",
  },
  "as-is.original": {
    label: "원본 구성",
    description: "원본에서 읽어낸 구성을 확인합니다 — 원본을 그대로 재현한 것이 아닙니다",
  },
  "design.system": {
    label: "시스템",
    description: "토큰과 디자인 시스템 규칙을 관리합니다",
  },
  "design.sources": {
    label: "소스",
    description: "Figma와 시각 소스 근거를 검토합니다",
  },
  "design.components": {
    label: "컴포넌트",
    description: "재사용 컴포넌트와 변형을 검토합니다",
  },
  "design.states": {
    label: "상태",
    description: "반응형 및 접근성 상태를 검토합니다",
  },
  "design.compare": {
    label: "비교",
    description: "소스와 생성 구조를 비교합니다",
  },
  "guide.overview": {
    label: "개요",
    description: "함께 제공되는 가이드를 검토합니다",
  },
  "guide.content": {
    label: "콘텐츠",
    description: "온보딩 및 운영 가이드를 작성합니다",
  },
  "guide.audiences": {
    label: "대상",
    description: "사용자 역할에 맞게 가이드를 조정합니다",
  },
  "guide.localization": {
    label: "현지화",
    description: "다국어 가이드를 관리합니다",
  },
  "guide.publish": {
    label: "게시",
    description: "승인된 가이드 결과물을 준비합니다",
  },
  "insights.overview": {
    label: "현황",
    description: "준비 상태와 온톨로지 범위를 검토합니다",
  },
  "insights.sufficiency": {
    label: "범위",
    description: "소스 근거가 충분한지 평가합니다",
  },
  "insights.quality": {
    label: "검사",
    description: "UI, 동작, 결과물 품질을 검토합니다",
  },
  "insights.runtime": {
    label: "런타임",
    description: "실행 추적과 실패를 확인합니다",
  },
  "insights.release": {
    label: "내보내기",
    description: "릴리스 차단 요소와 증거를 검토합니다",
  },
  "insights.live": {
    label: "운영",
    description: "운영 피드백과 검증 결과를 검토합니다",
  },
};

const JA_TASKS: StudioAuthorityTaskCopies = {
  "primary.app.overview": {
    label: "概要",
    description: "アプリケーションをプレビューして操作します",
  },
  "primary.app.structure": {
    label: "構造",
    description: "ページ、ブロック、要素を構成します",
  },
  "primary.app.behavior": {
    label: "動作",
    description: "イベントとActionFlowを定義します",
  },
  "primary.app.data": {
    label: "データ",
    description: "データソースとバインディングを接続します",
  },
  "primary.app.test": {
    label: "テスト",
    description: "アプリケーションの結果を検証します",
  },
  "primary.document.overview": {
    label: "概要",
    description: "ドキュメント成果物をプレビューします",
  },
  "primary.document.structure": {
    label: "構造",
    description: "ドキュメントのページとセクションを構成します",
  },
  "primary.document.content": {
    label: "コンテンツ",
    description: "ドキュメントの内容を作成、修正します",
  },
  "primary.document.sources": {
    label: "ソース",
    description: "根拠と引用を管理します",
  },
  "primary.document.export": {
    label: "エクスポート",
    description: "ネイティブ形式の書き出しを準備します",
  },
  "primary.game.overview": {
    label: "概要",
    description: "ゲームをプレビューしてプレイします",
  },
  "primary.game.scenes": {
    label: "シーン",
    description: "シーンとゲーム画面を構成します",
  },
  "primary.game.rules": {
    label: "ルール",
    description: "ゲームルールとActionFlowを定義します",
  },
  "primary.game.data": {
    label: "データ",
    description: "ゲームの状態とデータを管理します",
  },
  "primary.game.playtest": {
    label: "プレイテスト",
    description: "プレイ可能な動作を検証します",
  },
  "as-is.original": {
    label: "元の構成",
    description: "元画面から読み取った構成を確認します — 再現ではありません",
  },
  "design.system": {
    label: "システム",
    description: "トークンとデザインシステム規則を管理します",
  },
  "design.sources": {
    label: "ソース",
    description: "Figmaとビジュアル根拠を確認します",
  },
  "design.components": {
    label: "コンポーネント",
    description: "再利用可能なコンポーネントとバリアントを確認します",
  },
  "design.states": {
    label: "状態",
    description: "レスポンシブとアクセシビリティの状態を確認します",
  },
  "design.compare": {
    label: "比較",
    description: "ソースと生成構造を比較します",
  },
  "guide.overview": {
    label: "概要",
    description: "付属ガイドを確認します",
  },
  "guide.content": {
    label: "コンテンツ",
    description: "オンボーディングと運用ガイドを作成します",
  },
  "guide.audiences": {
    label: "対象",
    description: "ユーザーの役割に合わせてガイドを調整します",
  },
  "guide.localization": {
    label: "ローカライズ",
    description: "多言語ガイドを管理します",
  },
  "guide.publish": {
    label: "公開",
    description: "承認済みのガイド成果物を準備します",
  },
  "insights.overview": {
    label: "状況",
    description: "準備状況とオントロジー範囲を確認します",
  },
  "insights.sufficiency": {
    label: "範囲",
    description: "ソース根拠が十分か評価します",
  },
  "insights.quality": {
    label: "確認",
    description: "UI、動作、成果物の品質を確認します",
  },
  "insights.runtime": {
    label: "ランタイム",
    description: "実行トレースと失敗を確認します",
  },
  "insights.release": {
    label: "書き出し",
    description: "リリース阻害要因と証拠を確認します",
  },
  "insights.live": {
    label: "運用",
    description: "本番フィードバックと検証結果を確認します",
  },
};

const ZH_TASKS: StudioAuthorityTaskCopies = {
  "primary.app.overview": {
    label: "概览",
    description: "预览并操作应用",
  },
  "primary.app.structure": {
    label: "结构",
    description: "组织页面、区块和元素",
  },
  "primary.app.behavior": {
    label: "行为",
    description: "定义事件和ActionFlow",
  },
  "primary.app.data": {
    label: "数据",
    description: "连接数据源和绑定",
  },
  "primary.app.test": {
    label: "测试",
    description: "验证应用结果",
  },
  "primary.document.overview": {
    label: "概览",
    description: "预览文档成果",
  },
  "primary.document.structure": {
    label: "结构",
    description: "组织文档页面和章节",
  },
  "primary.document.content": {
    label: "内容",
    description: "撰写和修订文档内容",
  },
  "primary.document.sources": {
    label: "来源",
    description: "管理文档证据和引用",
  },
  "primary.document.export": {
    label: "导出",
    description: "准备原生文档导出",
  },
  "primary.game.overview": {
    label: "概览",
    description: "预览并试玩游戏",
  },
  "primary.game.scenes": {
    label: "场景",
    description: "组织场景和游戏画面",
  },
  "primary.game.rules": {
    label: "规则",
    description: "定义游戏规则和ActionFlow",
  },
  "primary.game.data": {
    label: "数据",
    description: "管理游戏状态和数据",
  },
  "primary.game.playtest": {
    label: "试玩",
    description: "验证可玩行为",
  },
  "as-is.original": {
    label: "原始结构",
    description: "查看从原始页面读取的结构 — 并非原样复现",
  },
  "design.system": {
    label: "系统",
    description: "管理令牌和设计系统规则",
  },
  "design.sources": {
    label: "来源",
    description: "检查Figma和视觉来源证据",
  },
  "design.components": {
    label: "组件",
    description: "检查可复用组件和变体",
  },
  "design.states": {
    label: "状态",
    description: "检查响应式和无障碍状态",
  },
  "design.compare": {
    label: "对比",
    description: "对比来源与生成结构",
  },
  "guide.overview": {
    label: "概览",
    description: "检查配套指南",
  },
  "guide.content": {
    label: "内容",
    description: "撰写引导和运营指南",
  },
  "guide.audiences": {
    label: "受众",
    description: "按用户角色调整指南",
  },
  "guide.localization": {
    label: "本地化",
    description: "管理多语言指南",
  },
  "guide.publish": {
    label: "发布",
    description: "准备已批准的指南成果",
  },
  "insights.overview": {
    label: "现状",
    description: "检查就绪状态和本体覆盖",
  },
  "insights.sufficiency": {
    label: "范围",
    description: "评估来源证据是否充分",
  },
  "insights.quality": {
    label: "检查",
    description: "检查UI、行为和成果质量",
  },
  "insights.runtime": {
    label: "运行时",
    description: "检查运行轨迹和故障",
  },
  "insights.release": {
    label: "导出",
    description: "检查发布阻碍和证明",
  },
  "insights.live": {
    label: "线上",
    description: "检查生产反馈和验证结果",
  },
};

const ZH_TW_TASKS: StudioAuthorityTaskCopies = {
  "primary.app.overview": {
    label: "概覽",
    description: "預覽並操作應用程式",
  },
  "primary.app.structure": {
    label: "結構",
    description: "組織頁面、區塊和元素",
  },
  "primary.app.behavior": {
    label: "行為",
    description: "定義事件和ActionFlow",
  },
  "primary.app.data": {
    label: "資料",
    description: "連接資料來源和綁定",
  },
  "primary.app.test": {
    label: "測試",
    description: "驗證應用程式結果",
  },
  "primary.document.overview": {
    label: "概覽",
    description: "預覽文件成果",
  },
  "primary.document.structure": {
    label: "結構",
    description: "組織文件頁面和章節",
  },
  "primary.document.content": {
    label: "內容",
    description: "撰寫和修訂文件內容",
  },
  "primary.document.sources": {
    label: "來源",
    description: "管理文件證據和引用",
  },
  "primary.document.export": {
    label: "匯出",
    description: "準備原生文件匯出",
  },
  "primary.game.overview": {
    label: "概覽",
    description: "預覽並試玩遊戲",
  },
  "primary.game.scenes": {
    label: "場景",
    description: "組織場景和遊戲畫面",
  },
  "primary.game.rules": {
    label: "規則",
    description: "定義遊戲規則和ActionFlow",
  },
  "primary.game.data": {
    label: "資料",
    description: "管理遊戲狀態和資料",
  },
  "primary.game.playtest": {
    label: "試玩",
    description: "驗證可玩行為",
  },
  "as-is.original": {
    label: "原始結構",
    description: "檢視從原始頁面讀取的結構 — 並非原樣重現",
  },
  "design.system": {
    label: "系統",
    description: "管理設計權杖和設計系統規則",
  },
  "design.sources": {
    label: "來源",
    description: "檢查Figma和視覺來源證據",
  },
  "design.components": {
    label: "元件",
    description: "檢查可重用元件和變體",
  },
  "design.states": {
    label: "狀態",
    description: "檢查響應式和無障礙狀態",
  },
  "design.compare": {
    label: "比較",
    description: "比較來源與生成結構",
  },
  "guide.overview": {
    label: "概覽",
    description: "檢查配套指南",
  },
  "guide.content": {
    label: "內容",
    description: "撰寫引導和營運指南",
  },
  "guide.audiences": {
    label: "受眾",
    description: "依使用者角色調整指南",
  },
  "guide.localization": {
    label: "在地化",
    description: "管理多語言指南",
  },
  "guide.publish": {
    label: "發佈",
    description: "準備已核准的指南成果",
  },
  "insights.overview": {
    label: "現況",
    description: "檢查就緒狀態和本體涵蓋",
  },
  "insights.sufficiency": {
    label: "範圍",
    description: "評估來源證據是否充分",
  },
  "insights.quality": {
    label: "檢查",
    description: "檢查UI、行為和成果品質",
  },
  "insights.runtime": {
    label: "執行階段",
    description: "檢查執行軌跡和故障",
  },
  "insights.release": {
    label: "匯出",
    description: "檢查發佈阻礙和證明",
  },
  "insights.live": {
    label: "線上",
    description: "檢查正式環境回饋和驗證結果",
  },
};

const STUDIO_AUTHORITY_TASK_COPY: Record<
  LocaleCode,
  StudioAuthorityTaskCopies
> = {
  en: EN_TASKS,
  ko: KO_TASKS,
  ja: JA_TASKS,
  zh: ZH_TASKS,
  "zh-TW": ZH_TW_TASKS,
};

/**
 * `hasSourceMarkup` (task-4 fix-round-2, default false = unchanged
 * behavior): when the As-Is domain's snapshot carries `sourceMarkup`, the
 * task copy this returns must say so, using the SAME words the As-Is pane
 * itself renders (`getCoogoAsIsPaneCopy`) rather than a third, independently
 * maintained "rail" string — that duplication is exactly how a live-browser
 * E2E (task-5) found the pane correctly saying "Original" while this rail's
 * description still said "not a reproduction of it" for the same snapshot.
 * Callers MUST derive `hasSourceMarkup` from `hasAsIsSourceMarkup`
 * (as-is-snapshot.ts), the single predicate `getCoogoAsIsArtifactLabel`
 * (the tab label) also derives from, so the tab and this rail can never
 * decide the question differently again.
 */
export function getStudioAuthorityTaskCopy(
  locale: LocaleCode,
  domain: StudioAuthorityDomain,
  outputKind: StudioProjectOutputKind,
  task: Pick<StudioAuthorityTask, "id" | "label" | "description">,
  hasSourceMarkup = false,
): StudioAuthorityTaskCopy {
  if (domain === "as-is" && hasSourceMarkup) {
    const paneCopy = getCoogoAsIsPaneCopy(locale)
    return { label: paneCopy.asIsSourceTruthHeading, description: paneCopy.asIsMarkupNote }
  }
  const key = (
    domain === "primary"
      ? `primary.${outputKind}.${task.id}`
      : `${domain}.${task.id}`
  ) as StudioAuthorityCopyKey
  const copy = STUDIO_AUTHORITY_TASK_COPY[locale][key] ?? task
  const renewalItem = getStudioRenewalRailItems(domain, outputKind).find(
    (item: StudioRenewalRailItem) => item.taskId === task.id,
  )
  return renewalItem ? { ...copy, label: getCoogoIdeRailLabel(locale, renewalItem.id) } : copy
}

export function getStudioAuthorityEditCopy(
  locale: LocaleCode,
  task: StudioAuthorityTaskCopy,
): StudioAuthorityTaskCopy {
  switch (locale) {
    case "ko":
      return {
        label: `${task.label} 편집`,
        description: `이 항목의 ${task.label} 작업 공간을 엽니다.`,
      };
    case "ja":
      return {
        label: `${task.label}を編集`,
        description: `この項目の${task.label}ワークスペースを開きます。`,
      };
    case "zh":
      return {
        label: `编辑${task.label}`,
        description: `打开此项目的${task.label}工作区。`,
      };
    case "zh-TW":
      return {
        label: `編輯${task.label}`,
        description: `開啟此項目的${task.label}工作區。`,
      };
    default:
      return {
        label: `Edit ${task.label.toLowerCase()}`,
        description: `Open the ${task.label.toLowerCase()} workspace for this item.`,
      };
  }
}
